import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';

/** True when the RTK error is qbot's 404 NOT_LINKED (no linked CR tag). */
function isNotLinked(error: unknown): boolean {
  const e = error as { status?: number; data?: { error?: { code?: string } } } | undefined;
  return e?.status === 404 && e?.data?.error?.code === 'NOT_LINKED';
}
function isRateLimited(error: unknown): boolean {
  return (error as { status?: number } | undefined)?.status === 429;
}

interface AdvisorOverlayProps {
  open: boolean;
  title: string;
  onClose: () => void;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  children: ReactNode;
}

/**
 * A scrollable overlay for a deterministic advisor result (Deck Doctor /
 * Counter), painted over the fixed deck stage so the frame never reflows. It
 * owns the shared states — loading, NOT_LINKED (link CTA), rate-limit, generic
 * error — and renders `children` (the result) only once data is ready.
 */
export function AdvisorOverlay({
  open,
  title,
  onClose,
  loading,
  error,
  onRetry,
  children,
}: AdvisorOverlayProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-20 flex flex-col rounded-xl bg-bg/95 backdrop-blur">
      <div className="flex shrink-0 items-center justify-between px-1 pb-1.5 pt-0.5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">{title}</span>
        <button
          type="button"
          aria-label={t('panel.close')}
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md bg-bg-secondary text-fg active:opacity-80"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0.5">
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
            <p className="max-w-[16rem] text-xs text-fg-muted">{t('panel.advisor.loading')}</p>
          </div>
        ) : error ? (
          isNotLinked(error) ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
              <p className="max-w-[16rem] text-sm text-fg-muted">{t('panel.link.prompt')}</p>
              <button
                type="button"
                onClick={() => {
                  haptics.impact('light');
                  navigate('/cr/link');
                }}
                className="rounded-lg bg-brand px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-fg"
              >
                {t('panel.link.cta')}
              </button>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <p className="text-sm text-rose-300">
                {isRateLimited(error) ? t('panel.advisor.rateLimited') : t('panel.advisor.error')}
              </p>
              <button
                type="button"
                onClick={onRetry}
                className="text-[11px] font-bold uppercase tracking-wide text-brand"
              >
                {t('panel.retry')}
              </button>
            </div>
          )
        ) : (
          children
        )}
      </div>
    </div>
  );
}
